import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import AliceProjectDetailsForm from './AliceProjectDetailsForm';
import AliceRecommendedEntities from './AliceRecommendedEntities';
import Scrollable from '../../../Shared/Edit/Scrollable';
import AliceRecommendedRelations from './AliceRecommendedRelations';
import { addSelectedDataToProject, createOnlyProject, getEntitiesExceedingAttributeLimitOnCreatingRelations } from './utils';
import useUser from '../../../../Hooks/User/useUser';
import useAliceGPT from '../../../../Hooks/AliceGPT/useAliceGPT';
import * as AliceGPTEvents from '../../../../Context/AliceGPT/AliceGPTContextEvents';
import useGetProjects from '../../../../Hooks/Projects/useGetProjects';
import useFeatureFlags from '../../../../Hooks/FeatureFlag/useFeatureFlags';
import { DEFAULT_PAGE_CONFIGURATION } from './variables';
import { USER_PROFILE_ENTITY } from './variables';
import ShuffleMessage from '../../../Shared/ShuffleMessage/ShuffleMessage';
import { bindShuffleMessageI18nEvents, messagesWhenCreatingProject } from '../../../Shared/ShuffleMessage/MessagesHelper';
import startsWithNumber from '../../../Helpers/Validators/startsWithNumber';
import { CreateProjectRequest } from './RequestTypes';
import { useTranslation } from 'react-i18next';
import { postMessageToRuntime } from '../../../Helpers/Runtime/runtime';
import { getCurrentSubscriptionForOrganisation } from '../../../../API/Payment/Payment';
import { bindI18nEvents as bindAttributeTypeDropdowni18n } from '../../../Attributes/Fields/AttributeTypeField';
import { bindI18nEvents as bindAttributeTypeFieldi18n } from '../../../Shared/Recommendation/AttributeTypeDropdown';
import EditPageConfiguration from './EditPageConfiguration';
import { LanguagesMap } from '../../../OrganizationSettings/LanguagesMap';
import { callApiWithAuthorization, callApiWithoutAuthorization } from '../../../../API/CallApi';
import RoleRecommendation from './RoleRecommendation';
import CalculatedFieldRecommendation from './CalculatedFieldRecommendation';
import PagesConfiguration from './PagesConfiguration';
import PortalSettingsSection from './PortalSettingsSection';
import { getDatalakeImageUrl } from '../../../Helpers/DateLakeStorage';
import Upgrade from '../../../Shared/Subscription/Upgrade';

function AliceGPTDialog({ organizationId, organizationName, toggleDialog, toggleUpgradeDialog, onCreate }) {

    const [state, dispatch] = useAliceGPT();

    const titles = [
        "Create App",
        "Create App - Alice AI Recommendation",
        "Create App - Alice AI Recommendation",
    ]

    async function handleCreate(request) {
        const project = await createOnlyProject(request);
        if (project._id) {
            await addSelectedDataToProject(
                request.organizationDetails.organizationId, project._id,
                request.entities, request.relations
            );
        }
        onCreate(project);
    }

    return (
        <Dialog
            title={titles[state.step]}
            height={"90%"}
            width={"80%"}
            onClose={toggleDialog}
        >
            <AliceGPTCreateProjectForm
                organizationId={organizationId}
                organizationName={organizationName}
                toggleDialog={toggleDialog}
                toggleUpgradeDialog={toggleUpgradeDialog}
                onCreate={handleCreate}
            />
        </Dialog>
    )
}

export function AliceGPTCreateProjectForm({
    organizationId,
    organizationName,
    isUsedInRuntime,
    defaultLanguage,
    onCreate,
    skipAIAndCreateProject,
    showAIFields = true,
    showLanguageDropdown = false,
    backToAppLibrary,
    showBackButton
}) {

    const { t } = useTranslation([
        "create_project/alice_gpt_dialog",
        "create_project/alice_project_details_form",
        "create_project/alice_recommended_entities",
        "create_project/alice_recommended_attributes_list",
        "create_project/alice_recommended_relations",
        "create_project/edit_page_configurations",
        "linked_entity/recommended_relation_card",
        "common/attribute_type_dropdown",
        "common/attribute_type_field",
        "attribute/attribute_type"
    ], { useSuspense: false });

    const descriptionRef = useRef(null);

    const [state, dispatch] = useAliceGPT();
    const featureFlags = useFeatureFlags();
    const [userLoading, user, userError] = useUser();
    const [projectsLoading, projects, projectsError] = useGetProjects(organizationId);

    const [creatingProject, setCreatingProject] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
    const [hasDuplicateRelations, setHasDuplicateRelations] = useState(false);

    useEffect(() => {
        bindShuffleMessageI18nEvents()
        getCurrentSubscriptionForOrganisation(organizationId)
            .then(subscription => {
                dispatch({
                    type: AliceGPTEvents.SUBSCRIPTION_LOADED,
                    payload: subscription
                })
            })
        bindAttributeTypeDropdowni18n()
        bindAttributeTypeFieldi18n()
        postMessageToRuntime({
            eventType: "STEP_CHANGED",
            value: state.step + 1,
            totalSteps: 7
        })
    }, []);

    useEffect(() => {
        if (defaultLanguage) {
            const language = LanguagesMap.find(lang => lang.code === defaultLanguage);
            dispatch({
                type: AliceGPTEvents.FORM_UPDATED,
                payload: {
                    ...state.form,
                    language: language?.code
                }
            })
        }
    }, [defaultLanguage])

    function isNextDisabled() {
        switch (state.step) {
            case 0:
                return (
                    !state.form.projectName.trim() ||
                    /[^a-zA-Z0-9 ]/g.test(state.form.projectName) ||
                    isProjectNameUsed() ||
                    entityWithErrorSelected() ||
                    startsWithNumber(state.form.projectName.trim())
                )
            case 1:
                const selectedEntities = state.entities.filter(entity => entity.checked);
                if (selectedEntities.length === 1) return true;
                if (hasOnlyHtmlAttributesSelected()) return true;
                const hasEmptyEntitiesName = selectedEntities.some(entity => entity.name.trim().length === 0);
                const hasEntitiesStartWithNumber = selectedEntities.some(entity => startsWithNumber(entity.name.trim()));
                const hasSameNameAsAnotherEntity = selectedEntities.some((entity, entityIndex) => {
                    if (entity.checked) {
                        const entityName = entity.name.trim().toLowerCase();
                        return selectedEntities.some((e, i) =>
                            e.name.trim().toLowerCase() === entityName &&
                            i !== entityIndex
                        );
                    }
                    return false;
                });
                const entityHasAttributesWithSameName = selectedEntities.some(entity => {
                    if (entity.checked) {
                        const attributes = entity.attributes.filter(attribute => attribute.selected);
                        const attributeNames = attributes.map(attribute => attribute.name.trim().toLowerCase());
                        return attributeNames.some(name => attributeNames.filter(n => n === name).length > 1);
                    }
                    return false;
                });
                const entityWithNoPagesSelected = selectedEntities.some(entity => (
                    Object.values(entity.pageConfiguration).every(value => !value)
                ))
                if (hasSameNameAsAnotherEntity || entityHasAttributesWithSameName || hasEntitiesStartWithNumber || hasEmptyEntitiesName) return true;
                if (entityWithNoPagesSelected) return false;
                return isCreateAppDisabled();
            case 2:
                const selectedRelations = state.relations.filter(relation => relation.selected);
                const selectedEntitiesWithNoPagesSelected = state.entities.some(entity => (
                    entity.checked &&
                    Object.values(entity.pageConfiguration).every(value => !value)
                ));
                if (selectedEntitiesWithNoPagesSelected) return false;
                const relationWithNoRefAttrSelected = state.relations.find(relation => relation.showReferenceAttribute && !relation.referenceAttribute)
                if (relationWithNoRefAttrSelected) {
                    return true;
                }
                const entitiesWithAttributeLimitExceeded = getEntitiesExceedingAttributeLimitOnCreatingRelations(
                    state.entities,
                    state.relations,
                    state.subscription
                )
                if (entitiesWithAttributeLimitExceeded.length > 0) {
                    return true;
                }
                return (
                    state.error ||
                    !state.initialAttributesLoaded ||
                    selectedRelations.length > state.subscription.maxRelationsPerApp ||
                    hasDuplicateRelations
                );
            case 3:
                // Check if page configuration is still loading
                if (state.isPageConfigurationLoading) {
                    return true;
                }
                return false;
            case 4:
                // Check if page configuration is still loading
                if (state.shouldLoadPortals) {
                    return true;
                }
                const selectedPortals = state.portals.filter(portal => portal.entities.some(entity => entity.checked));
                if (selectedPortals.length > state.subscription.maxPortalsPerApp) {
                    return true;
                }
                return false;
            default:
                return false;
        }
    }

    function isCreateAppDisabled() {
        // Check for loading states based on current step
        switch (state.step) {
            case 0:
                if (
                    !state.form.projectName.trim() ||
                    /[^a-zA-Z0-9 ]/g.test(state.form.projectName) ||
                    isProjectNameUsed() ||
                    entityWithErrorSelected() ||
                    startsWithNumber(state.form.projectName.trim())
                ) {
                    return true;
                }
                break;
            case 2:
                if (hasDuplicateRelations) {
                    return true;
                }
                break;
            case 3:
                // Check if page configuration is still loading
                if (state.isPageConfigurationLoading) {
                    return true;
                }
                break;
            case 4:
                // Check if page configuration is still loading
                if (state.shouldLoadPortals) {
                    return true;
                }
                const selectedPortals = state.portals.filter(portal => portal.entities.some(entity => entity.checked));
                if (selectedPortals.length > state.subscription.maxPortalsPerApp) {
                    return true;
                }
                break;
            case 6:
                if (state.rolesLoading) {
                    return true;
                }
                break;
            default:
                break;
        }

        const selectedEntities = state.entities.filter(entity => entity.checked);
        const isInLoading = selectedEntities.some(entity => entity.attributesLoading);
        if (isInLoading) return true;
        if (hasOnlyHtmlAttributesSelected()) return true;
        const hasSameNameAsAnotherEntity = selectedEntities.some((entity, entityIndex) => {
            if (entity.checked) {
                const entityName = entity.name.trim().toLowerCase();
                return selectedEntities.some((e, i) =>
                    e.checked &&
                    e.name.trim().toLowerCase() === entityName &&
                    i !== entityIndex
                );
            }
            return false;
        });
        const hasEmptyEntitiesName = selectedEntities.some(entity => entity.name.trim().length === 0);
        const hasEntitiesStartWithNumber = selectedEntities.some(entity => startsWithNumber(entity.name.trim()));
        const entityHasAttributesWithSameName = selectedEntities.some(entity => {
            if (entity.checked) {
                const attributes = entity.attributes.filter(attribute => attribute.selected);
                const attributeNames = attributes.map(attribute => attribute.name.trim().toLowerCase());
                return attributeNames.some(name => attributeNames.filter(n => n === name).length > 1);
            }
            return false;
        });
        const entityWithMoreThanSubscriptionAttributes = selectedEntities.some(entity =>
            entity.attributes.filter(attribute => attribute.selected).length > state.subscription.maxAttributesPerApp
        );
        const entityWithNoAttributesSelected = selectedEntities.some(entity => entity.attributes.filter(attribute => attribute.selected).length === 0);
        const entityWithMoreThanOneStateAttribute = selectedEntities.some(entity => entity.attributes.filter(attribute => attribute.selected && attribute.type === "State Field").length > 1);
        const entityWithPicklistOrStateWith0AttributeSelected = selectedEntities.some(entity => (
            entity.attributes.find(attribute => (
                attribute.selected &&
                (attribute.type === "Picklist String" || attribute.type === "State Field") &&
                (!attribute.picklist || attribute.picklist.length === 0)
            ))
        ))
        const entityWithAttributesNameWithError = selectedEntities.some(entity => (
            entity.attributes.some(attribute => (
                attribute.selected &&
                (attribute.name.trim().length === 0 || /^\d/g.test(attribute.name))
            ))
        ))
        const entityWithSameAttributesName = selectedEntities.some(entity => {
            if (entity.checked) {
                const attributes = entity.attributes.filter(attribute => attribute.selected);
                const attributeNames = attributes.map(attribute => attribute.name.trim().toLowerCase());
                return attributeNames.some(name => attributeNames.filter(n => n === name).length > 1);
            }
            return false;
        });
        const hasEntityWithMoreThan30Chars = selectedEntities.some(entity => entity.name.trim().length > 30);
        const entityHasAttributesWithMoreThan30Chars = selectedEntities.some(entity => {
            if (entity.checked) {
                const attributes = entity.attributes.filter(attribute => attribute.selected);
                const attributeNames = attributes.map(attribute => attribute.name.trim());
                return attributeNames.some(name => name.length > 30);
            }
            return false;
        });

        const entityWithNoPagesSelected = selectedEntities.some(entity => (
            Object.values(entity.pageConfiguration).every(value => !value)
        ))
        if (state.step === 3 && state.isPageConfigurationLoading) {
            return true;
        }

        if (state.step === 2) {
            const entitiesWithAttributeLimitExceeded = getEntitiesExceedingAttributeLimitOnCreatingRelations(
                state.entities,
                state.relations,
                state.subscription
            )
            if (entitiesWithAttributeLimitExceeded.length > 0) {
                return true;
            }
        }
        return (
            selectedEntities.length > state.subscription.maxEntitiesPerApp ||
            entityWithMoreThanSubscriptionAttributes ||
            entityWithNoAttributesSelected ||
            entityWithMoreThanOneStateAttribute ||
            entityWithPicklistOrStateWith0AttributeSelected ||
            hasSameNameAsAnotherEntity ||
            entityWithSameAttributesName ||
            hasEmptyEntitiesName ||
            hasEntitiesStartWithNumber ||
            entityHasAttributesWithSameName ||
            entityWithAttributesNameWithError ||
            entityWithNoPagesSelected ||
            hasEntityWithMoreThan30Chars ||
            entityHasAttributesWithMoreThan30Chars
        )
    }

    function isMaxAppsReached() {
        return projects.length >= state.subscription.maxApps
    }

    function isProjectNameUsed() {
        return projects.some(project => project.name.trim().toLowerCase() === state.form.projectName.trim().toLowerCase());
    }

    function entityWithErrorSelected() {
        return state.entities.some(entity => entity.hasAttributeLoadingError && entity.checked);
    }

    function hasOnlyHtmlAttributesSelected() {
        const selectedEntities = state.entities.filter(entity => entity.checked);
        return selectedEntities.some(entity => {
            const selectedAttributes = entity.attributes.filter(attribute => attribute.selected);
            // If no attributes are selected, this check doesn't apply
            if (selectedAttributes.length === 0) {
                return false;
            }
            // Check if all selected attributes are HTML type
            return selectedAttributes.length > 0 && selectedAttributes.every(attribute => attribute.type === "Html");
        });
    }

    function toggleCreatingProject() {
        setCreatingProject(!creatingProject);
    }

    function handleCreateButtonClick() {
        if (isMaxAppsReached()) {
            setShowUpgradeDialog(true);
        } else {
            handleSubmit();
        }
    }

    function handleSkipAndCreateButtonClick() {
        if (isMaxAppsReached()) {
            setShowUpgradeDialog(true);
        } else {
            skipRecommendationAndCreateProject();
        }
    }

    const handleSubmit = async () => {
        // toggleCreatingProject();

        const organizationDetails = getOrganizationDetails();
        const projectProperties = getProjectProperties();
        const userProperties = getUserProperties();

        const selectedEntities = getSelectedEntities();
        const selectedRelations = getSelectedRelations();

        const entitiesAndAttributes = selectedEntities.map((entity) => {
            const pageConfiguration = state.step === 3 || entity.isUserEntity ? entity.pageConfiguration : DEFAULT_PAGE_CONFIGURATION;
            const entityFormulas = state.formulaRecommendations[entity.name] ? state.formulaRecommendations[entity.name].attributes : [];
            return {
                name: entity.name?.trim(),
                icon: entity.icon,
                isUserEntity: entity.isUserEntity,
                attributes: [
                    ...entity.attributes.map((attribute) => {
                        let picklist = attribute.picklist;
                        if (attribute.type === "State Field" && attribute.picklist.length === 0) {
                            picklist = ["Not Started", "In Progress", "Completed"];
                        }
                        return {
                            name: attribute.name?.trim(),
                            type: attribute.type,
                            picklist: picklist,
                            required: attribute.type === "State Field" ? true : attribute.required,
                            unique: attribute.unique,
                            isAzureAttribute: attribute.isAzureAttribute,
                            sysName: attribute.sysName
                        }
                    }),
                    ...entityFormulas.map(attr => ({
                        name: attr.attributeName?.trim(),
                        type: attr.attributeType,
                        formula: attr.formula
                    }))
                ],
                pageConfiguration
            }
        });

        const configurations = selectedEntities.map(entity => ({
            entityName: entity.name,
            ...entity.pageConfiguration
        }));

        const portals = state.portals.filter(portal => portal.entities.some(entity => entity.checked)).slice(0, state.subscription.maxPortalsPerApp) || [];
        const selectedRoles = state.subscription.rolesPermission === "Full access" ? state.roles.filter(role => state.selectedRoles.includes(role.key)) : [];
        const portalSettings = state.portalSettings;

        debugger

        const request = new CreateProjectRequest(
            organizationDetails, projectProperties, userProperties,
            entitiesAndAttributes, selectedRelations, portals, configurations,
            selectedRoles,
            portalSettings
        );

        onCreate(request);
    }

    async function skipRecommendationAndCreateProject() {
        // toggleCreatingProject();
        postMessageToRuntime({
            eventType: "RECOMMENDATION_SKIPPED",
            value: true
        })
        postMessageToRuntime({
            eventType: "PROJECT_NAME_CHANGED",
            value: state.form.projectName
        })
        const organizationDetails = getOrganizationDetails();
        const projectProperties = getProjectProperties();
        const userProperties = getUserProperties();

        const selectedEntities = [USER_PROFILE_ENTITY];
        const selectedRelations = [];

        const entitiesAndAttributes = selectedEntities.map((entity) => ({
            name: entity.name?.trim(),
            icon: entity.icon,
            isUserEntity: entity.isUserEntity,
            attributes: entity.attributes.map((attribute) => ({
                name: attribute.name?.trim(),
                type: attribute.type,
                picklist: attribute.picklist,
                required: attribute.type === "State Field" ? true : attribute.required,
                unique: attribute.unique,
                isAzureAttribute: attribute.isAzureAttribute,
                sysName: attribute.sysName
            })),
            pageConfiguration: entity.pageConfiguration
        }));

        const request = new CreateProjectRequest(
            organizationDetails, projectProperties, userProperties,
            entitiesAndAttributes, selectedRelations
        );

        skipAIAndCreateProject(request);
    }

    function getOrganizationDetails() {
        const organizationDetails = { organizationId, organizationName };
        return organizationDetails;
    }

    function getProjectProperties() {
        return {
            ...state.form,
            language: state.form.language || "en"
        };
    }

    function getUserProperties() {
        const email = user.username ? user.username : user.emails[0];
        const userProperties = { email };
        return userProperties;
    }

    function getSelectedEntities() {
        return state.entities.filter(entity => entity.checked).map(entity => ({
            ...entity,
            attributes: entity.attributes
                .filter(attribute => attribute.selected)
                .slice(0, state.subscription.maxAttributesPerApp)
        })).slice(0, state.subscription.maxEntitiesPerApp);
    }

    function getSelectedRelations() {
        return state.relations.filter(relation => relation.selected).slice(0, state.subscription.maxRelationsPerApp);
    }

    function showActions() {
        if (state.step === 2) {
            if (creatingProject) return false;
            const selectedEntities = state.entities.filter(entity => entity.checked);
            return selectedEntities.length > 1 ? state.relationsLoaded : true;
        }
        return !creatingProject;
    }

    // function isThereEntityWithDuplicateNames() {
    //     const selectedEntities = state.entities.filter(entity => entity.checked);
    //     return selectedEntities.some((entity, entityIndex) => {
    //         const entityName = entity.name.trim().toLowerCase();
    //         return selectedEntities.some((e, i) =>
    //             e.name.trim().toLowerCase() === entityName &&
    //             i !== entityIndex
    //         );
    //     });
    // }

    // Add handler for PortalSettingsSection
    function handlePortalMenuOrientationChange(orientation) {
        dispatch({ type: AliceGPTEvents.UPDATE_PORTAL_MENU_ORIENTATION, payload: orientation });
    }
    function handlePortalLogoChange(file, preview) {
        dispatch({ type: AliceGPTEvents.UPDATE_PORTAL_LOGO, payload: { file, preview } });
    }
    function handlePortalSettingsReset() {
        dispatch({ type: AliceGPTEvents.RESET_PORTAL_SETTINGS });
    }
    function handlePortalSettingsApply(settings) {
        dispatch({ type: AliceGPTEvents.UPDATE_PORTAL_MENU_ORIENTATION, payload: settings.menuOrientation });
        dispatch({ type: AliceGPTEvents.UPDATE_PORTAL_LOGO, payload: { file: settings.logoFile, preview: settings.logoPreview } });
    }

    const stepContent = [
        <AliceProjectDetailsForm
            isUsedInRuntime={isUsedInRuntime}
            showAIFields={showAIFields}
            showLanguageDropdown={showLanguageDropdown}
            checkDuplicateProjectName={isProjectNameUsed}
        />,
        <AliceRecommendedEntities descriptionRef={descriptionRef} />,
        <AliceRecommendedRelations descriptionRef={descriptionRef} onDuplicateChange={setHasDuplicateRelations} />,
        <PagesConfiguration />,
        <EditPageConfiguration
            useAI={showAIFields}
            maxPortals={state.subscription && state.subscription.maxPortalsPerApp ? state.subscription.maxPortalsPerApp : 1}
        />,
        <PortalSettingsSection />,
        <RoleRecommendation
            entities={state.entities}
            maxRoles={state.subscription.maxRolesPerApp || 3}
            descriptionRef={descriptionRef}
        />,
    ]

    // handle next and handle back are intentionally placed here
    // so that they can access the stepContent array for calculating it's length and sending it to runtime
    async function handleNext() {
        if (state.step === 0 && state.form.projectName.trim()) {
            postMessageToRuntime({
                eventType: "PROJECT_NAME_CHANGED",
                value: state.form.projectName
            })
            if (state.selectedFile && !state.fileProcessedForDescription) {
                setLoading(true);
                const formData = new FormData();
                formData.append('file', state.selectedFile);
    
                try {
                    const request = await callApiWithAuthorization('/api/v0/alicegpt/extract-description-from-file', {
                        method: 'POST',
                        body: formData,
                    });
                    const response = await request.json();
                    debugger
                    if (response.description && response.description !== "false") {
                        descriptionRef.current = response.description;
                    }
                    // Mark the file as processed
                    dispatch({ type: AliceGPTEvents.FILE_PROCESSED_FOR_DESCRIPTION });
                } catch (error) {
                    console.error('Error uploading file:', error);
                }

                setLoading(false);
            }
        } else if (state.step === 1) {
            const numSelectedEntities = state.entities.filter(entity => entity.checked).length;
            if (numSelectedEntities <= 2) {
                dispatch({ type: AliceGPTEvents.NEXT });
            }
        } else if (state.step === 2) {
            if (!state.isPageConfigurationLoading) {
                dispatch({
                    type: AliceGPTEvents.TOGGLE_DISPLAY_CONFIGURATION_LOADED
                })
            }
        }
        postMessageToRuntime({
            eventType: "STEP_CHANGED",
            // here there is +2 because we are sending 1st step when mounted.
            // and at this point in code, state.step has not changed yet.
            // it'll be changed after the next line of code.
            value: state.step + 2,
            totalSteps: stepContent.length
        })
        dispatch({ type: AliceGPTEvents.NEXT });
    }

    function handleBack() {
        if (state.step === 3) {
            const numSelectedEntities = state.entities.filter(entity => entity.checked).length;
            if (numSelectedEntities <= 2) {
                dispatch({ type: AliceGPTEvents.BACK });
            }
            dispatch({
                type: AliceGPTEvents.TOGGLE_DISPLAY_CONFIGURATION_LOADED
            })
        }
        postMessageToRuntime({
            eventType: "STEP_CHANGED",
            // here there is no +2 because we are sending 1st step when mounted.
            // and at this point in code, state.step has not changed yet.
            // it'll be changed after the next line of code.
            value: state.step,
            totalSteps: stepContent.length
        })
        dispatch({ type: AliceGPTEvents.BACK })
    }

    const stepDialogActions = [
        <div>
            <div className='mx-3 d-flex justify-content-between'>
                {
                    showAIFields ?
                        <>
                            <div>
                                {showBackButton &&
                                    <button className='btn btn-outline-secondary' onClick={backToAppLibrary}>
                                        {t("buttons.back_to_Library")}
                                    </button>
                                }
                            </div>
                            <div>
                                <button className='btn btn-outline-primary' onClick={handleSkipAndCreateButtonClick} disabled={isCreateAppDisabled()}>
                                    {t("buttons.skip_and_create_now")}
                                </button>
                                <button className='btn btn-primary ms-2' autoFocus={true} onClick={handleNext} disabled={isNextDisabled()}>
                                    {t("buttons.next")}
                                </button>
                            </div>
                        </>
                        :
                        <button
                            style={{ position: "relative", left: "90%" }}
                            className='btn btn-primary'
                            onClick={handleSubmit}
                            disabled={isNextDisabled()}
                        >
                            {t("buttons.create_app")}
                        </button>
                }
            </div>
        </div >,
        <div>
            <div className='mx-3 d-flex justify-content-between'>
                {
                    state.isLoadingEntities ?
                        <>
                            <div>
                                <button className='btn btn-outline-secondary' onClick={handleBack}>
                                    {t("buttons.back")}
                                </button>
                            </div>
                            <div>
                                <button className='btn btn-primary ms-2' autoFocus={true} onClick={handleNext} disabled>
                                    {t("buttons.next")}
                                </button>
                            </div>
                        </>
                        :
                        <>
                            <div>
                                <button className='btn btn-outline-secondary' onClick={handleBack}>
                                    {t("buttons.back")}
                                </button>
                            </div>
                            <div>
                                {/* {
                                    state.entities.some(entity => (
                                        entity.attributes.filter(attribute => (
                                            attribute.selected && attribute.type === "State Field"
                                        )).length > 1
                                    )) &&
                                    <span className='text-danger'>
                                        {t("errors.only_one_state_allowed")}
                                    </span>
                                } */}
                                {/* {
                                    isThereEntityWithDuplicateNames() &&
                                    <span className='text-danger'>
                                        {t("errors.duplicate_entity_names")}
                                    </span>
                                } */}
                                &nbsp;&nbsp;
                                <button className='btn btn-outline-primary' disabled={isCreateAppDisabled()} onClick={handleCreateButtonClick}>
                                    {t("buttons.create_app")}
                                </button>
                                <button className='btn btn-primary ms-2' autoFocus={true} onClick={handleNext} disabled={isNextDisabled()}>
                                    {t("buttons.next")}
                                </button>
                            </div>
                        </>
                }
            </div>
        </div>,
        <div>
            <div className='mx-3 d-flex justify-content-between'>
                <div>
                    <button className='btn btn-outline-secondary' onClick={handleBack}>
                        {t("buttons.back")}
                    </button>
                </div>
                <div>
                    <button className='btn btn-outline-primary' onClick={handleCreateButtonClick} disabled={isCreateAppDisabled()}>
                        {t("buttons.create_app")}
                    </button>
                    <button className='btn btn-primary ms-2' autoFocus={true} onClick={handleNext} disabled={isNextDisabled()}>
                        {t("buttons.next")}
                    </button>
                </div>
            </div>
        </div>,
        <div>
            <div className='mx-3 d-flex justify-content-between'>
                <div>
                    <button className='btn btn-outline-secondary' onClick={handleBack}>
                        {t("buttons.back")}
                    </button>
                </div>
                <div>
                    <button className='btn btn-outline-primary' onClick={handleCreateButtonClick} disabled={isCreateAppDisabled()}>
                        {t("buttons.create_app")}
                    </button>
                    <button className='btn btn-primary ms-2' autoFocus={true} onClick={handleNext} disabled={isNextDisabled()}>
                        {t("buttons.next")}
                    </button>
                </div>
            </div>
        </div>,
        <div>
            <div className='mx-3 d-flex justify-content-between'>
                <div>
                    <button className='btn btn-outline-secondary' onClick={handleBack}>
                        {t("buttons.back")}
                    </button>
                </div>
                <div>
                    <button className='btn btn-outline-primary' onClick={handleCreateButtonClick} disabled={isCreateAppDisabled()}>
                        {t("buttons.create_app")}
                    </button>
                    <button className='btn btn-primary ms-2' autoFocus={true} onClick={handleNext} disabled={isNextDisabled()}>
                        {t("buttons.next")}
                    </button>
                </div>
            </div>
        </div>,
        <div>
            <div className='mx-3 d-flex justify-content-between'>
                <div>
                    <button className='btn btn-outline-secondary' onClick={handleBack}>
                        {t("buttons.back")}
                    </button>
                </div>
                <div>
                    <button className='btn btn-outline-primary' onClick={handleCreateButtonClick} disabled={isCreateAppDisabled()}>
                        {t("buttons.create_app")}
                    </button>
                    <button className='btn btn-primary ms-2' autoFocus={true} onClick={handleNext} disabled={isNextDisabled()}>
                        {t("buttons.next")}
                    </button>
                </div>
            </div>
        </div>,
        <div>
            <div className='mx-3 d-flex justify-content-between'>
                <div>
                    <button className='btn btn-outline-secondary' onClick={handleBack}>
                        {t("buttons.back")}
                    </button>
                </div>
                <div>
                    <button className='btn btn-primary' onClick={handleCreateButtonClick} disabled={isCreateAppDisabled()}>
                        {t("buttons.create_app")}
                    </button>
                </div>
            </div>
        </div>
    ]

    if (loading) {
        return (
            <div className='d-flex flex-column justify-content-center align-items-center h-100'>
                <img src={getDatalakeImageUrl('loaders/RecommendationLoader.gif')} alt="" />
                <h3 className="mt-4">
                    {t("loading_message", { ns: "create_project/alice_recommended_entities" })}
                </h3>
            </div>
        );
    }

    return (
        <>
            <div style={{ height: "90%" }}>
                {
                    creatingProject ?
                        <div className="d-flex flex-column justify-content-enter align-items-center w-100 h-100">
                            <div style={{ height: "80%" }}>
                                <img
                                    alt="loader-clone"
                                    height={"100%"}
                                    src={`${process.env.REACT_APP_DATALAKESTORAGE_URL}/${process.env.REACT_APP_DATALAKESTORAGE_RESOURCES}/images/loaders/library.gif`} />
                            </div>
                            <ShuffleMessage
                                data={messagesWhenCreatingProject}
                                interval={3000} />
                        </div>
                        :
                        <Scrollable>
                            {stepContent[state.step]}
                        </Scrollable>
                }
            </div>
            <div className='pt-3' style={{ borderTop: '1px solid #e0e0e0' }}>
                {
                    showActions() && stepDialogActions[state.step]
                }
            </div>
            {showUpgradeDialog && (
                <Upgrade
                    toggleDialog={() => setShowUpgradeDialog(false)}
                    organization={{ id: organizationId }}
                    Headline={'Need to build <br/>more apps? '}
                    Type={'Pro'}
                />
            )}
        </>
    )
}

export default AliceGPTDialog;